import DownloadsActions from 'reducers/Downloads'
import NotificationActions from 'reducers/Notification'
import { AudioService } from 'services'
import {
  DownloadTaskType,
  DownloadTaskStatus,
  NotificationType,
} from 'constants/enums'
import { defaultBitrate, tokenExpiryThreshold } from 'config'
import axios from 'axios'
// @ts-expect-error uuid v3 subpath import
import uuidv4 from 'uuid/v4'
import last from 'lodash/last'
import { dateHelpers } from 'utils'
import { getPageSource, normalizeValue, trackDownloadEvent } from 'utils/analytics'

import jwtd from 'jwt-decode'
import type { AppDispatch } from 'types/store'

const getSuffix = (download: any, format: string) => {
  if (download.type === DownloadTaskType.song) {
    return `.${format}`
  } else {
    return '.zip'
  }
}

const checkIfTokenIsValid = (token: string) => {
  if (!token) return false
  try {
    const dToken: any = jwtd(token)
    const expiresAt = Number(dToken?.exp)
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return false

    // Convert to milis if the timeStamp in the claim is in seconds
    const expiry = expiresAt * 10 ** (13 - `${expiresAt}`.length)
    return expiry >= Date.now() + dateHelpers.seconds(tokenExpiryThreshold)
  } catch {
    return false
  }
}

const updateDownloadTaskForRelease = (download: any, response: any, dispatch: AppDispatch) => {
  const url = response.data.zipped_url
  const token = last(url.split('/'))
  dispatch(
    DownloadsActions.updateDownload(download, {
      token,
      url,
      progress: 0,
    })
  )
}

const updateDownloadTaskForSong = (download: any, response: any, dispatch: AppDispatch) => {
  const token = response.data.token

  let url = AudioService.getAudioDownloadURL(token)

  if (download.type === DownloadTaskType.video) {
    url = AudioService.getVideoDownloadURL(token)
  }

  dispatch(
    DownloadsActions.updateDownload(download, {
      token,
      url,
      progress: 0,
    })
  )
}

const refreshTokenIfNeeded = (download: any, dispatch: AppDispatch) => {
  return new Promise<any>((resolve, reject) => {
    if (
      checkIfTokenIsValid(download.token) &&
      (download.type !== DownloadTaskType.playlist || download.url)
    ) {
      resolve(download)
    } else if (download.type === DownloadTaskType.release) {
      AudioService.getAudioZipper(String(download.mediaID), {
        format: download.format,
      })
        .then((response: any) => {
          updateDownloadTaskForRelease(download, response, dispatch)
          resolve(download)
        })
        .catch(() => {
          reject()
        })
    } else if (
      download.type === DownloadTaskType.song ||
      download.type === DownloadTaskType.video
    ) {
      AudioService.getToken(String(download.mediaID))
        .then((response: any) => {
          updateDownloadTaskForSong(download, response, dispatch)
          resolve(download)
        })
        .catch((e: any) => {
          console.warn(e)
          reject()
        })
    } else if (download.type === DownloadTaskType.playlist) {
      AudioService.getPlaylistZipper(String(download.mediaID), {
        format: download.format,
      })
        .then((response: any) => {
          const url = response?.data?.zipped_url
          const token = typeof url === 'string' ? last(url.split('/')) : null
          if (!url || !token) throw new Error('Missing playlist download URL')

          updateDownloadTaskForRelease(download, response, dispatch)
          resolve({ ...download, token, url, progress: 0 })
        })
        .catch(() => {
          reject()
        })
    }
  })
}

const refreshSongTokenIfNeeded = (download: any, dispatch: AppDispatch) => {
  return new Promise<void>((resolve, reject) => {
    if (
      download.type === DownloadTaskType.song ||
      download.type === DownloadTaskType.video
    ) {
      AudioService.getToken(String(download.mediaID))
        .then((response: any) => {
          updateDownloadTaskForSong(download, response, dispatch)
          resolve()
        })
        .catch((e: any) => {
          console.warn(e)
          reject()
        })
    }
  })
}

function getExplicitLabel(download: any) {
  return download?.explicit ? ' [E]' : ''
}

export const startDownload =
  (download: any, source = 'player') =>
  (dispatch: AppDispatch) => {
    const format = download?.format ? download?.format : 'mp3'
    const explicitLabel = getExplicitLabel(download)
    const resolvedSource = normalizeValue(
      download?.sourceLabel || source || getPageSource() || 'player'
    )
    if (download.type === DownloadTaskType.playlist) {
      dispatch(
        DownloadsActions.updateStatus(download, DownloadTaskStatus.loading)
      )
    }

    //first refresh the token of the download and then start. Token can be expired due to the throttling of number of downloads.
    refreshTokenIfNeeded(download, dispatch)
      .then((preparedDownload) => {
        const suffix = getSuffix(download, format)
        dispatch(
          DownloadsActions.updateStatus(download, DownloadTaskStatus.loading)
        )
        trackDownloadEvent('Download Start', download, {
          format: normalizeValue(format),
          source: resolvedSource,
        })
        axios({
          url: download.type === DownloadTaskType.playlist
            ? preparedDownload.url
            : download.url,
          cancelToken: download.source.token,
          method: 'GET',
          responseType: 'blob',
          params: { format, bitrate: defaultBitrate },
          onDownloadProgress: (progressEvent: any) => {
            const percentCompleted = Math.floor(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            dispatch(
              DownloadsActions.updateProgress(download, percentCompleted || 0)
            )
          },
        })
          .then((response) => {
            dispatch(
              NotificationActions.broadcastMessage(
                download.artist === ''
                  ? download.title
                  : download.artist + ' - ' + download.title,
                NotificationType.downloadFinished
              )
            )

            const blob = new Blob([response.data], {
              type: response.data.type,
            })

            if (typeof (window.navigator as any).msSaveBlob !== 'undefined') {
              ;(window.navigator as any).msSaveBlob(blob, download.title + suffix)
            } else {
              const blobURL = window.URL.createObjectURL(blob)
              const tempLink = document.createElement('a')
              const downloadVersion = download.version
                ? ` (${download.version})`
                : ''
              const downloadOrdinal = download.ordinal
                ? `${download.ordinal} - `
                : ''
              tempLink.style.display = 'none'
              tempLink.id = 'downloadReady'
              tempLink.href = blobURL
              tempLink.setAttribute(
                'download',
                `${download.artist} - ${downloadOrdinal}${download.title}${explicitLabel}${downloadVersion}${suffix}`
              )
              if (typeof tempLink.download === 'undefined') {
                tempLink.setAttribute('target', '_blank')
              }
              document.body.appendChild(tempLink)
              tempLink.click()
              document.body.removeChild(tempLink)
              window.URL.revokeObjectURL(blobURL)
            }

            dispatch(
              DownloadsActions.updateStatus(download, DownloadTaskStatus.finished)
            )
            trackDownloadEvent('Download Complete', download, {
              format: normalizeValue(format),
              source: resolvedSource,
            })
          })
          .catch((thrown) => {
            if (axios.isCancel(thrown)) {
              // TODO: notification download cancelled
            } else {
              // TODO: notification
              dispatch(
                DownloadsActions.updateStatus(download, DownloadTaskStatus.failed)
              )
              trackDownloadEvent('Download Failed', download, {
                format: normalizeValue(format),
                source: resolvedSource,
              })
            }
          })
      })
      .catch((e) => {
        if (download.type === DownloadTaskType.playlist) {
          dispatch(
            DownloadsActions.updateStatus(download, DownloadTaskStatus.failed)
          )
          trackDownloadEvent('Download Failed', download, {
            format: normalizeValue(format),
            source: resolvedSource,
          })
        } else {
          console.warn(e)
        }
      })
  }

export const startVideoDownload =
  (download: any, format: string, source = 'player') =>
  (dispatch: AppDispatch) => {
    const resolvedSource = normalizeValue(
      download?.sourceLabel || source || getPageSource() || 'player'
    )
    //first refresh the token of the download and then start. Token can be expired due to the throttling of number of downloads.
    refreshTokenIfNeeded(download, dispatch)
      .then(() => {
        dispatch(
          DownloadsActions.updateStatus(download, DownloadTaskStatus.loading)
        )
        trackDownloadEvent('Download Start', download, {
          format: normalizeValue(format),
          source: resolvedSource,
        })
        // fetch assets url
        axios({
          url: download.url,
          cancelToken: download.source.token,
          method: 'GET',
        })
          // download video file
          .then((res) => {
            const suffix = res.data.url.substring(res.data.url.length - 4)
            axios({
              url: res.data.url,
              method: 'GET',
              responseType: 'blob',
              onDownloadProgress: (progressEvent: any) => {
                const percentCompleted = Math.floor(
                  (progressEvent.loaded * 100) / progressEvent.total
                )
                dispatch(
                  DownloadsActions.updateProgress(download, percentCompleted || 0)
                )
              },
            }).then((response) => {
              dispatch(
                NotificationActions.broadcastMessage(
                  download.artist === ''
                    ? download.title
                    : download.artist + ' - ' + download.title,
                  NotificationType.downloadFinished
                )
              )

              const blob = new Blob([response.data], {
                type: response.data.type,
              })

              if (typeof (window.navigator as any).msSaveBlob !== 'undefined') {
                ;(window.navigator as any).msSaveBlob(blob, download.title + suffix)
              } else {
                const blobURL = window.URL.createObjectURL(blob)
                const tempLink = document.createElement('a')
                const downloadVersion = download.version
                  ? ` (${download.version})`
                  : ''
                const downloadOrdinal = download.ordinal
                  ? `${download.ordinal} - `
                  : ''
                tempLink.style.display = 'none'
                tempLink.id = 'downloadReady'
                tempLink.href = blobURL
                tempLink.setAttribute(
                  'download',
                  `${download.artist} - ${downloadOrdinal}${download.title}${downloadVersion}${suffix}`
                )
                if (typeof tempLink.download === 'undefined') {
                  tempLink.setAttribute('target', '_blank')
                }
                document.body.appendChild(tempLink)
                tempLink.click()
                document.body.removeChild(tempLink)
                window.URL.revokeObjectURL(blobURL)
              }

              dispatch(
                DownloadsActions.updateStatus(
                  download,
                  DownloadTaskStatus.finished
                )
              )
              trackDownloadEvent('Download Complete', download, {
                format: normalizeValue(format),
                source: resolvedSource,
              })
            })
          })
          .catch((thrown) => {
            if (axios.isCancel(thrown)) {
              // TODO: notification download cancelled
            } else {
              // TODO: notification
              dispatch(
                DownloadsActions.updateStatus(download, DownloadTaskStatus.failed)
              )
              trackDownloadEvent('Download Failed', download, {
                format: normalizeValue(format),
                source: resolvedSource,
              })
            }
          })
      })
      .catch((e) => console.warn(e))
  }

export const asyncQueryZipperReady =
  (downloadTask: any, successCallBack?: () => void, failureCallBack?: (e?: any) => void) =>
  (
    dispatch: AppDispatch,
    getState?: () => { downloads: { downloads: any[] } }
  ) => {
    if (typeof downloadTask.token !== 'string' || !downloadTask.token) {
      return undefined
    }

    const isCurrentTask = () => {
      if (!getState) return true

      const current = getState().downloads.downloads.find((download) =>
        download.downloadID === downloadTask.downloadID
      )
      return current?.status === DownloadTaskStatus.waiting &&
        current.token === downloadTask.token
    }

    const handleResponse = (response: any) => {
      if (!isCurrentTask()) return

      const { status, progress } = response.data
      if (status === 'done') {
        dispatch(
          DownloadsActions.updateDownload(downloadTask, {
            zipperReady: true,
          })
        )
        if (successCallBack) {
          successCallBack()
        }
      } else {
        dispatch(
          DownloadsActions.updateDownload(downloadTask, {
            isZipping: true,
            zippingProgress: progress,
          })
        )
        if (failureCallBack) {
          failureCallBack()
        }
      }
    }
    const handleError = (e: any) => {
      if (!isCurrentTask()) return

      if (downloadTask.type === DownloadTaskType.playlist) {
        dispatch(
          DownloadsActions.updateStatus(downloadTask, DownloadTaskStatus.failed)
        )
        trackPlaylistDownloadEvent('Download Failed', downloadTask)
      }
      if (failureCallBack) {
        failureCallBack(e)
      }
    }
    if (downloadTask.type === DownloadTaskType.playlist) {
      return AudioService.getPlaylistZipperStatus(String(downloadTask.token))
        .then(handleResponse)
        .catch(handleError)
    } else if (downloadTask.type === DownloadTaskType.release) {
      return AudioService.getAudioZipperStatus(String(downloadTask.token))
        .then(handleResponse)
        .catch(handleError)
    }
    return undefined
  }

const getMatchingPlaylistDownloads = (downloads: any[], playlistID: any, format: string) =>
  downloads.filter((download) =>
    download.type === DownloadTaskType.playlist &&
    String(download.mediaID) === String(playlistID) &&
    download.format === format
  )

const trackPlaylistDownloadEvent = (event: string, download: any) => {
  try {
    trackDownloadEvent(event, download, {
      format: normalizeValue(download.format),
      source: normalizeValue(download.sourceLabel),
    })
  } catch (error) {
    console.warn(error)
  }
}

const preparePlaylistDownload = async (download: any, dispatch: AppDispatch) => {
  try {
    return await refreshTokenIfNeeded(download, dispatch)
  } catch {
    dispatch(DownloadsActions.updateStatus(download, DownloadTaskStatus.failed))
    trackPlaylistDownloadEvent('Download Failed', download)
    return null
  }
}

const restartPlaylistDownload = (
  download: any,
  source: string,
  dispatch: AppDispatch
) => {
  const update = {
    source: axios.CancelToken.source(),
    sourceLabel: source,
    status: DownloadTaskStatus.waiting,
    progress: 0,
    zipperReady: false,
    isZipping: false,
    zippingProgress: 0,
    token: undefined,
    url: undefined,
  }
  const restartedDownload = { ...download, ...update }
  dispatch(DownloadsActions.updateDownload(download, update))
  trackPlaylistDownloadEvent('Download Restart', restartedDownload)
  return preparePlaylistDownload(restartedDownload, dispatch)
}

export const addPlaylistToDownloadQueue =
  (playlist: any, format = 'mp3', sourceLabel = 'player') =>
  async (
    dispatch: AppDispatch,
    getState?: () => { downloads: { downloads: any[] } }
  ) => {
    if (format !== 'mp3' && format !== 'wav') return null

    const matching = getMatchingPlaylistDownloads(
      getState?.()?.downloads?.downloads || [],
      playlist.playlistid,
      format
    )
    const activeDownload = matching.find((download) =>
      download.status === DownloadTaskStatus.waiting ||
      download.status === DownloadTaskStatus.loading
    )
    if (activeDownload) return activeDownload

    const resolvedSourceLabel = sourceLabel || getPageSource() || 'player'
    const { name, coverart, tracks } = playlist
    const failedDownload = matching.find((download) =>
      download.status === DownloadTaskStatus.failed
    )
    if (failedDownload) {
      const metadata = { title: name, trackcount: tracks, coverart }
      dispatch(DownloadsActions.updateDownload(failedDownload, metadata))
      return restartPlaylistDownload(
        { ...failedDownload, ...metadata },
        resolvedSourceLabel,
        dispatch
      )
    }

    const source = axios.CancelToken.source()
    const download = {
      type: DownloadTaskType.playlist,
      artist: '',
      title: name,
      trackcount: tracks,
      coverart,
      mediaID: playlist.playlistid,
      downloadID: uuidv4(),
      status: DownloadTaskStatus.waiting,
      progress: 0,
      source,
      sourceLabel: resolvedSourceLabel,
      format: format,
    }
    dispatch(DownloadsActions.addDownload(download))
    dispatch(
      NotificationActions.broadcastMessage(
        name,
        NotificationType.addDownloadQueue
      )
    )
    trackPlaylistDownloadEvent('Download Queued', download)
    return preparePlaylistDownload(download, dispatch)
  }

export const addReleaseToDownloadQueue =
  (release: any, format = 'mp3', sourceLabel = 'player') =>
  (dispatch: AppDispatch) => {
    const source = axios.CancelToken.source()
    const resolvedSourceLabel = sourceLabel || getPageSource() || 'player'
    const download = {
      type: DownloadTaskType.release,
      artist: release.artist,
      title: release.title,
      explicit: release.explicit,
      is_ai_generated: release.is_ai_generated,
      trackcount: release.trackcount,
      coverart: release.coverart,
      mediaID: release.releaseid,
      downloadID: uuidv4(),
      status: DownloadTaskStatus.waiting,
      progress: 0,
      format,
      source,
      sourceLabel: resolvedSourceLabel,
    }
    dispatch(DownloadsActions.addDownload(download))
    dispatch(
      NotificationActions.broadcastMessage(
        release.artist + ' - ' + release.title,
        NotificationType.addDownloadQueue
      )
    )
    trackDownloadEvent('Download Queued', download, {
      format: normalizeValue(format),
      source: normalizeValue(resolvedSourceLabel),
    })
    refreshTokenIfNeeded(download, dispatch)
  }

export const addSongToDownloadQueue =
  (song: any, params: { format?: string; source?: string } = { format: 'mp3', source: 'player' }) =>
  (dispatch: AppDispatch) => {
    const source = axios.CancelToken.source()
    const { format, source: sourceLabel } = params
    const resolvedSourceLabel = sourceLabel || getPageSource() || 'player'
    const download = {
      type: DownloadTaskType.song,
      artist: song.artist,
      title: song.title,
      version: song.version,
      explicit: song.explicit,
      is_ai_generated: song.is_ai_generated,
      ordinal: song.ordinal,
      trackcount: 1,
      coverart: song.coverart,
      token: song.token,
      mediaID: song.trackid,
      downloadID: uuidv4(),
      status: DownloadTaskStatus.waiting,
      progress: 0,
      format,
      source,
      sourceLabel: resolvedSourceLabel,
    }
    dispatch(DownloadsActions.addDownload(download))
    dispatch(
      NotificationActions.broadcastMessage(
        song.artist + ' - ' + song.title,
        NotificationType.addDownloadQueue
      )
    )
    trackDownloadEvent('Download Queued', download, {
      format: normalizeValue(format),
      source: normalizeValue(resolvedSourceLabel),
    })
    refreshSongTokenIfNeeded(download, dispatch)
  }

// For downloading series of tracks instead of zipped release (without notification)
export const addTrackToDownloadQueue =
  (song: any, format = 'mp3', sourceLabel = 'player') =>
  (dispatch: AppDispatch) => {
    const source = axios.CancelToken.source()
    const resolvedSourceLabel = sourceLabel || getPageSource() || 'player'
    const download = {
      type: DownloadTaskType.song,
      artist: song.artist,
      title: song.title,
      version: song.version,
      explicit: song.explicit,
      is_ai_generated: song.is_ai_generated,
      ordinal: song.ordinal,
      trackcount: 1,
      coverart: song.coverart,
      token: song.token,
      mediaID: song.trackid,
      downloadID: uuidv4(),
      status: DownloadTaskStatus.waiting,
      progress: 0,
      format,
      source,
      sourceLabel: resolvedSourceLabel,
    }
    dispatch(DownloadsActions.addDownload(download))

    refreshSongTokenIfNeeded(download, dispatch)
  }

export const addVideoToDownloadQueue =
  (song: any, sourceLabel = 'player') =>
  (dispatch: AppDispatch) => {
    const source = axios.CancelToken.source()
    const resolvedSourceLabel = sourceLabel || getPageSource() || 'player'
    const download = {
      type: DownloadTaskType.video,
      artist: song.artist,
      title: song.title,
      version: song.version,
      is_ai_generated: song.is_ai_generated,
      ordinal: song.ordinal,
      trackcount: 1,
      coverart: song.coverart,
      token: song.token,
      mediaID: song.trackid,
      downloadID: uuidv4(),
      status: DownloadTaskStatus.waiting,
      progress: 0,
      source,
      sourceLabel: resolvedSourceLabel,
    }
    dispatch(DownloadsActions.addDownload(download))
    dispatch(
      NotificationActions.broadcastMessage(
        song.artist + ' - ' + song.title,
        NotificationType.addDownloadQueue
      )
    )
    trackDownloadEvent('Download Queued', download, {
      format: normalizeValue(song?.format),
      source: normalizeValue(resolvedSourceLabel),
    })
    refreshSongTokenIfNeeded(download, dispatch)
  }

export const restartDownload =
  (download: any, source = 'player') =>
  (
    dispatch: AppDispatch,
    getState?: () => { downloads: { downloads: any[] } }
  ) => {
    if (download.type === DownloadTaskType.playlist) {
      const activeDownload = getMatchingPlaylistDownloads(
        getState?.()?.downloads?.downloads || [],
        download.mediaID,
        download.format
      ).find((item) =>
        item.status === DownloadTaskStatus.waiting ||
        item.status === DownloadTaskStatus.loading
      )
      if (activeDownload) return Promise.resolve(activeDownload)

      return restartPlaylistDownload(
        download,
        source || getPageSource() || 'player',
        dispatch
      )
    }

    const cancelSource = axios.CancelToken.source()
    const resolvedSource = source || getPageSource() || 'player'
    dispatch(
      DownloadsActions.updateDownload(download, {
        source: cancelSource,
        sourceLabel: resolvedSource,
        status: DownloadTaskStatus.waiting,
        progress: 0,
      })
    )
    trackDownloadEvent('Download Restart', download, {
      format: normalizeValue(download.format),
      source: normalizeValue(resolvedSource),
    })
    return undefined
  }

export const removeDownload = (download: any) => (dispatch: AppDispatch) => {
  if (download.status === 'loading') download.source.cancel()
  dispatch(DownloadsActions.removeDownload(download))
}

export default {
  addPlaylistToDownloadQueue,
  addReleaseToDownloadQueue,
  addSongToDownloadQueue,
  addTrackToDownloadQueue,
  addVideoToDownloadQueue,
  removeDownload,
  startDownload,
  startVideoDownload,
  restartDownload,
  asyncQueryZipperReady,
}
